'use client'

import type { ReactNode } from 'react'
import * as React from 'react'
import { useStore } from 'zustand'
import { createStore } from 'zustand/vanilla'

export interface DrawerState {
  isOpen: boolean
}

export interface DrawerActions {
  toggleDrawer: () => void
  setIsOpen: (isOpen: boolean) => void
}

export type DrawerStore = DrawerState & DrawerActions

export const initDrawerStore = (): DrawerState => {
  return { isOpen: false }
}

export const defaultInitState: DrawerState = {
  isOpen: false,
}

export const createDrawerStore = (initState: DrawerState = defaultInitState) => {
  return createStore<DrawerStore>()((set) => ({
    ...initState,
    toggleDrawer: () => set((state) => ({ isOpen: !state.isOpen })),
    setIsOpen: (isOpen: boolean) => set({ isOpen }),
  }))
}

export type DrawerStoreApi = ReturnType<typeof createDrawerStore>

export const DrawerStoreContext = React.createContext<DrawerStoreApi | undefined>(undefined)

export interface DrawerStoreProviderProps {
  children: ReactNode
}

export const DrawerStoreProvider = ({ children }: DrawerStoreProviderProps) => {
  const storeRef = React.useRef<DrawerStoreApi>(null)

  storeRef.current ??= createDrawerStore(initDrawerStore())

  return <DrawerStoreContext.Provider value={storeRef.current}>{children}</DrawerStoreContext.Provider>
}

export const useDrawerStore = <T,>(selector: (store: DrawerStore) => T): T => {
  const drawerStoreContext = React.useContext(DrawerStoreContext)

  if (!drawerStoreContext) {
    throw new Error('useDrawerStore must be used within DrawerStoreProvider')
  }

  return useStore(drawerStoreContext, selector)
}
